/**
 * Internal helper function to convert image blob to PNG blob
 * @param {Blob} imageBlob - The input image blob
 * @param {string} mimeType - The MIME type for the output
 * @param {number} quality - Quality parameter (0-1, default: 1)
 * @returns {Promise<Blob>} - A promise that resolves to the converted blob
 */
async function convertImageBlob(imageBlob, mimeType = 'image/png', quality = 1) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const imageUrl = URL.createObjectURL(imageBlob);
    
    const cleanup = () => URL.revokeObjectURL(imageUrl);
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          cleanup();
          reject(new Error('Failed to get 2D context from canvas'));
          return;
        }
        
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        
        ctx.drawImage(img, 0, 0);
        
        canvas.toBlob((convertedBlob) => {
          cleanup();
          
          if (convertedBlob) {
            resolve(convertedBlob);
          } else {
            reject(new Error(`Failed to convert canvas to ${mimeType} blob`));
          }
        }, mimeType, quality);
        
      } catch (error) {
        cleanup();
        reject(error);
      }
    };
    
    img.onerror = () => {
      cleanup();
      reject(new Error('Failed to load image'));
    };
    
    img.src = imageUrl;
  });
}

/**
 * Converts a JPG blob to PNG blob using HTML5 Canvas
 * @param {Blob} jpgBlob - The input JPG blob
 * @returns {Promise<Blob>} - A promise that resolves to the PNG blob
 */
export async function convertJpgToPng(jpgBlob) {
  if (!(jpgBlob instanceof Blob)) {
    throw new Error('Input must be a Blob');
  }
  
  return convertImageBlob(jpgBlob, 'image/png', 1);
}

/**
 * Converts a JPG blob to PNG blob with quality control
 * @param {Blob} jpgBlob - The input JPG blob
 * @param {number} quality - PNG quality (0-1, default: 1)
 * @returns {Promise<Blob>} - A promise that resolves to the PNG blob
 */
export async function convertJpgToPngWithQuality(jpgBlob, quality = 1) {
  if (!(jpgBlob instanceof Blob)) {
    throw new Error('Input must be a Blob');
  }
  
  if (quality < 0 || quality > 1) {
    throw new Error('Quality must be between 0 and 1');
  }
  
  return convertImageBlob(jpgBlob, 'image/png', quality);
}

/**
 * Converts any image blob to PNG blob
 * @param {Blob} imageBlob - The input image blob
 * @param {number} quality - PNG quality (0-1, default: 1)
 * @returns {Promise<Blob>} - A promise that resolves to the PNG blob
 */
export async function convertToPng(imageBlob, quality = 1) {
  if (!(imageBlob instanceof Blob)) {
    throw new Error('Input must be a Blob');
  }
  
  if (quality < 0 || quality > 1) {
    throw new Error('Quality must be between 0 and 1');
  }
  
  return convertImageBlob(imageBlob, 'image/png', quality);
}

/**
 * Compresses and resizes an image blob.
 *
 * Defaults are tuned for clipboard use: high fidelity, lossless for
 * screenshots/text (PNG), and high-quality JPEG for photos.
 *
 * @param {Blob} imageBlob - The input image blob
 * @param {number} maxWidth - Maximum width (default: 2560)
 * @param {number} maxHeight - Maximum height (default: 1440)
 * @param {number} quality - Compression quality (0-1, default: 0.92)
 * @param {string} mimeType - Output MIME type; pass null to auto-detect (default: null)
 * @returns {Promise<Blob>} - A promise that resolves to the compressed blob
 */
export async function compressImage(imageBlob, maxWidth = 2560, maxHeight = 1440, quality = 0.92, mimeType = null) {
  // Auto-detect output format:
  // - PNG input (screenshots, text) → keep as PNG (lossless, no artefacts)
  // - Everything else → JPEG at high quality
  const isPng = imageBlob.type === 'image/png';
  const outputMime = mimeType ?? (isPng ? 'image/png' : 'image/jpeg');
  // PNG ignores quality param (lossless), so set to 1 to avoid confusion
  const outputQuality = outputMime === 'image/png' ? 1 : quality;
  if (!(imageBlob instanceof Blob)) {
    throw new Error('Input must be a Blob');
  }
  
  if (quality < 0 || quality > 1) {
    throw new Error('Quality must be between 0 and 1');
  }
  
  return new Promise((resolve, reject) => {
    const img = new Image();
    const imageUrl = URL.createObjectURL(imageBlob);
    
    const cleanup = () => URL.revokeObjectURL(imageUrl);
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          cleanup();
          reject(new Error('Failed to get 2D context from canvas'));
          return;
        }
        
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;
        
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width *= ratio;
          height *= ratio;
        }
        
        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob((compressedBlob) => {
          cleanup();
          
          if (compressedBlob) {
            resolve(compressedBlob);
          } else {
            reject(new Error(`Failed to compress image to ${outputMime}`));
          }
        }, outputMime, outputQuality);
        
      } catch (error) {
        cleanup();
        reject(error);
      }
    };
    
    img.onerror = () => {
      cleanup();
      resolve(imageBlob); // Return original if compression fails
    };
    
    img.src = imageUrl;
  });
}

/**
 * Converts a canvas element to blob
 * @param {HTMLCanvasElement} canvas - The canvas element
 * @param {string} mimeType - Output MIME type (default: 'image/png')
 * @param {number} quality - Quality parameter (0-1, default: 1)
 * @returns {Promise<Blob>} - A promise that resolves to the blob
 */
export async function canvasToBlob(canvas, mimeType = 'image/png', quality = 1) {
  if (!canvas || !(canvas instanceof HTMLCanvasElement)) {
    throw new Error('Input must be a valid HTMLCanvasElement');
  }
  
  if (quality < 0 || quality > 1) {
    throw new Error('Quality must be between 0 and 1');
  }
  
  return new Promise((resolve, reject) => {
    if (canvas.toBlob) {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error(`Failed to convert canvas to ${mimeType} blob`));
        }
      }, mimeType, quality);
    } else {
      // Fallback for browsers without toBlob support
      try {
        const dataUrl = canvas.toDataURL(mimeType, quality);
        const byteString = atob(dataUrl.split(',')[1]);
        const mimeString = dataUrl.split(',')[0].split(':')[1].split(';')[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i += 1) {
          ia[i] = byteString.charCodeAt(i);
        }
        resolve(new Blob([ab], { type: mimeString }));
      } catch (error) {
        reject(new Error(`Failed to convert canvas to ${mimeType} blob: ${error.message}`));
      }
    }
  });
}
